import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import {
  parameterUtils,
  hardwareUtils,
  type ModelData,
  type ModelParameter,
  type ModelParameterUpdate,
  type HardwareItem,
} from './model-utils'
import type { PermissionAction } from '@/entities/auth/model'
import type { Attachment } from './create-entity-store'

// ─── Types ───

export interface ModelStoreState {
  // Data
  modelList: ModelData[]
  model: ModelData
  initialModel: ModelData
  permission: PermissionAction
  attachments: Attachment[]
  errorData: Record<string, any>
  services: unknown[]
  materials: unknown[]
  isLoading: boolean

  // Actions — common
  setModelList: (list: ModelData[]) => void
  setModel: (model: ModelData) => void
  setInitialModel: (model: ModelData) => void
  setField: (field: string, value: unknown) => void
  setErrorData: (field: string, value: unknown) => void
  setErrorDataObject: (errorData: Record<string, unknown>) => void
  setServices: (payload: { serviceNames?: string[]; serviceIds?: (number | null)[] }) => void
  setMaterials: (materials: unknown[]) => void
  reset: () => void
  setPermission: (permission: PermissionAction) => void
  setLoading: (loading: boolean) => void
  setDocs: (payload: Attachment[], own?: boolean) => void
  resetDocs: () => void

  // Actions — parameters
  updateParameter: (positionId: string, updates: ModelParameterUpdate) => void
  updateParameterBulk: (
    positionId: string,
    updates: ModelParameterUpdate,
    applyToSameType?: boolean,
  ) => void
  updateHardwareBulk: (
    parentPositionId: string,
    hardwareType: string,
    hardwarePositionId: string,
    updates: Partial<HardwareItem>,
    applyToSameType?: boolean,
  ) => void
  updateHardwarePosition: (
    parentPositionId: string,
    hardwareType: string,
    hardwarePositionId: string,
    position: Partial<{ x: number; y: number; z: number }>,
  ) => void
  updateAllFrameProfilesBulk: (updates: ModelParameterUpdate) => void
  updateDoorProfilesBulk: (
    doorPrefix: string,
    updates: ModelParameterUpdate,
    excludeDimensions?: boolean,
  ) => void
  restoreParametersToInitial: (
    excludePositionId: string,
    elementType: string,
    isHardware?: boolean,
    hardwareType?: string | null,
  ) => void

  // Actions — dimensions (model-specific)
  updateDimensions: (
    updateType: string,
    value: number | string,
    options?: Record<string, any>,
  ) => void
}

// ─── Dimension handler type ───

export type DimensionHandler = (
  model: ModelData,
  initialModel: ModelData,
  value: number,
  options: Record<string, any>,
) => ModelData

export interface CreateModelStoreOptions {
  initialModel: ModelData
  dimensionHandlers?: Record<string, DimensionHandler>
  extraActions?: (
    set: (fn: (state: ModelStoreState & Record<string, any>) => void) => void,
    get: () => ModelStoreState & Record<string, any>,
  ) => Record<string, any>
}

// ─── Attachment formatting (shared with create-entity-store) ───

function formatAttachment(d: Attachment): Attachment {
  const uf = d.uploadedFile
  return {
    id: d.attachmentId || d.id || uf?.attachmentId || uf?.id,
    attachmentId: d.attachmentId || d.id || uf?.attachmentId || uf?.id,
    docTypeId: d.docTypeId || uf?.docTypeId,
    docTypeName: d.docTypeName || uf?.docTypeName,
    extension: d.extension || uf?.extension,
    fileOriginalName: d.fileOriginalName || uf?.fileOriginalName,
    name: d.name || uf?.name,
    path: d.path || uf?.path,
    size: d.size || uf?.size,
    createdAt: d.createdAt,
    uploadedBy: d.uploadedBy || uf?.uploadedBy,
  }
}

// Deep copy that works with both plain objects and Immer drafts
function deepCopy<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

// ─── Factory ───

export function createModelStore(options: CreateModelStoreOptions) {
  const { initialModel, dimensionHandlers = {}, extraActions } = options

  return create<ModelStoreState>()(
    immer((set, get) => {
      const base: ModelStoreState = {
        // State
        modelList: [],
        model: deepCopy(initialModel),
        initialModel: deepCopy(initialModel),
        permission: {},
        attachments: [],
        errorData: {},
        services: [],
        materials: [],
        isLoading: false,

        // ─── Common actions ───

        setModelList: (list) =>
          set((s) => {
            s.modelList = list as typeof s.modelList
          }),

        setModel: (model) =>
          set((s) => {
            if (s.model === model) return
            s.model = model as typeof s.model
          }),

        setInitialModel: (model) =>
          set((s) => {
            if (s.initialModel === model && s.model === model) return
            s.initialModel = model as typeof s.initialModel
            s.model = model as typeof s.model
          }),

        setField: (field, value) =>
          set((s) => {
            ;(s.model as Record<string, any>)[field] = value
          }),

        setErrorData: (field, value) =>
          set((s) => {
            ;(s.errorData as Record<string, any>)[field] = value
          }),

        setErrorDataObject: (errorData) =>
          set((s) => {
            s.errorData = errorData
          }),

        setServices: (payload) =>
          set((s) => {
            ;(s.model as Record<string, any>).serviceNames = payload.serviceNames || []
            ;(s.model as Record<string, any>).serviceIds = payload.serviceIds || []
          }),

        setMaterials: (materials) =>
          set((s) => {
            s.materials = materials
          }),

        reset: () =>
          set((s) => {
            s.model = deepCopy(s.initialModel) as typeof s.model
            s.attachments = []
          }),

        setPermission: (permission) =>
          set((s) => {
            s.permission = permission
          }),

        setLoading: (loading) =>
          set((s) => {
            s.isLoading = loading
          }),

        setDocs: (payload, own = false) =>
          set((s) => {
            if (own) {
              s.attachments = payload
            } else {
              const formatted = payload.map(formatAttachment)
              s.attachments = s.attachments.length ? [...formatted, ...s.attachments] : formatted
            }
          }),

        resetDocs: () =>
          set((s) => {
            s.attachments = []
          }),

        // ─── Parameter actions ───

        updateParameter: (positionId, updates) =>
          set((s) => {
            if (!s.model.parameters) return
            s.model.parameters = parameterUtils.updateParameterInArray(
              s.model.parameters,
              positionId,
              updates,
            ) as typeof s.model.parameters
          }),

        updateParameterBulk: (positionId, updates, applyToSameType = false) =>
          set((s) => {
            if (!s.model.parameters) return

            let updatedParams = parameterUtils.updateParameterInArray(
              s.model.parameters,
              positionId,
              updates,
            )

            if (applyToSameType) {
              const selected = parameterUtils.findParameter(s.model.parameters, positionId)
              if (selected?.type) {
                updatedParams = parameterUtils.updateParametersByType(
                  updatedParams,
                  selected.type,
                  updates,
                  positionId,
                )
              }
            }

            s.model.parameters = updatedParams as typeof s.model.parameters
          }),

        updateHardwareBulk: (
          parentPositionId,
          hardwareType,
          hardwarePositionId,
          updates,
          applyToSameType = false,
        ) =>
          set((s) => {
            if (!s.model.parameters) return

            const paramIndex = parameterUtils.findParameterIndex(
              s.model.parameters,
              parentPositionId,
            )
            if (paramIndex === -1) return

            const updatedParams = [...s.model.parameters]
            // invariant: paramIndex !== -1 guarded above
            updatedParams[paramIndex] = hardwareUtils.updateHardwareInParameter(
              updatedParams[paramIndex]!,
              hardwareType,
              hardwarePositionId,
              updates,
            )

            if (applyToSameType) {
              const { elementType } = hardwareUtils.getHardwareTypeFromElement(
                s.model.parameters,
                hardwarePositionId,
              )
              if (elementType) {
                for (let i = 0; i < updatedParams.length; i++) {
                  // invariant: i < updatedParams.length
                  const param = updatedParams[i]!
                  if (param.hardware?.[hardwareType]) {
                    const updatedHw = param.hardware[hardwareType]!.map((hw) => {
                      if (hw.type === elementType && hw.positionId !== hardwarePositionId) {
                        return { ...hw, ...updates }
                      }
                      return hw
                    })
                    updatedParams[i] = {
                      ...param,
                      hardware: { ...param.hardware, [hardwareType]: updatedHw },
                    }
                  }
                }
              }
            }

            s.model.parameters = updatedParams as typeof s.model.parameters
          }),

        updateHardwarePosition: (parentPositionId, hardwareType, hardwarePositionId, position) =>
          set((s) => {
            if (!s.model.parameters) return

            s.model.parameters = s.model.parameters.map((param) => {
              if (param.positionId === parentPositionId && param.hardware?.[hardwareType]) {
                const updatedHw = param.hardware[hardwareType]!.map((hw) => {
                  if (hw.positionId === hardwarePositionId) {
                    return { ...hw, position: { ...hw.position, ...position } }
                  }
                  return hw
                })
                return { ...param, hardware: { ...param.hardware, [hardwareType]: updatedHw } }
              }
              return param
            }) as typeof s.model.parameters
          }),

        updateAllFrameProfilesBulk: (updates) =>
          set((s) => {
            if (!s.model.parameters) return
            s.model.parameters = s.model.parameters.map((param) => {
              if (
                param.type === 'PROFILE' &&
                param.positionId?.includes('frame_profile') &&
                !param.positionId?.includes('glass')
              ) {
                return { ...param, ...updates }
              }
              return param
            }) as typeof s.model.parameters
          }),

        updateDoorProfilesBulk: (doorPrefix, updates, excludeDimensions = false) =>
          set((s) => {
            if (!s.model.parameters) return
            s.model.parameters = s.model.parameters.map((param) => {
              if (param.positionId?.startsWith(doorPrefix)) {
                const filtered = { ...updates }
                if (excludeDimensions) {
                  delete (filtered as Record<string, any>).width
                  delete (filtered as Record<string, any>).height
                }
                return { ...param, ...filtered }
              }
              return param
            }) as typeof s.model.parameters
          }),

        restoreParametersToInitial: (
          excludePositionId,
          elementType,
          isHardware = false,
          hardwareType = null,
        ) =>
          set((s) => {
            if (!s.model.parameters || !s.initialModel.parameters) return

            if (isHardware && hardwareType) {
              s.model.parameters = s.model.parameters.map((param) => {
                const initialParam = s.initialModel.parameters!.find(
                  (ip) => ip.positionId === param.positionId,
                )
                if (param.hardware?.[hardwareType] && initialParam?.hardware?.[hardwareType]) {
                  const restoredHw = param.hardware[hardwareType]!.map((hw) => {
                    if (hw.type === elementType && hw.positionId !== excludePositionId) {
                      const initialHw = initialParam.hardware![hardwareType]!.find(
                        (ih) => ih.positionId === hw.positionId,
                      )
                      if (initialHw) {
                        return {
                          ...hw,
                          material: initialHw.material,
                          materialId: initialHw.materialId,
                          color: initialHw.color,
                          thickness: initialHw.thickness,
                        }
                      }
                    }
                    return hw
                  })
                  return { ...param, hardware: { ...param.hardware, [hardwareType]: restoredHw } }
                }
                return param
              }) as typeof s.model.parameters
            } else {
              s.model.parameters = s.model.parameters.map((param) => {
                if (param.type === elementType && param.positionId !== excludePositionId) {
                  const initialParam = s.initialModel.parameters!.find(
                    (ip) => ip.positionId === param.positionId,
                  )
                  if (initialParam) {
                    return {
                      ...param,
                      material: initialParam.material,
                      materialId: initialParam.materialId,
                      color: initialParam.color,
                      thickness: initialParam.thickness,
                    }
                  }
                }
                return param
              }) as typeof s.model.parameters
            }
          }),

        // ─── Dimensions (delegates to model-specific handlers) ───

        updateDimensions: (updateType, value, options = {}) =>
          set((s) => {
            const handler = dimensionHandlers[updateType]
            if (!handler) return

            try {
              const updatedModel = handler(
                deepCopy(s.model) as ModelData,
                deepCopy(s.initialModel) as ModelData,
                typeof value === 'string' ? parseInt(value) || 0 : value,
                options,
              )
              s.model = updatedModel as typeof s.model
            } catch {
              // Dimension calculation failed — keep current state
            }
          }),
      }

      // Merge extra actions if provided
      if (extraActions) {
        return {
          ...base,
          ...extraActions(set, get as () => ModelStoreState & Record<string, any>),
        } as ModelStoreState
      }

      return base
    }),
  )
}
